import type { ReactNode } from 'react';
import { ResearchActivityDisplay } from './ResearchActivityDisplay';
import type { ResearchActivityItem } from '../../handler/TextRenderer/researchActivity';
import '../../Styles/Message/ResearchActivityPreview.css';

interface PreviewState {
  title: string;
  description: string;
  badge: string;
  isActive: boolean;
  items: ResearchActivityItem[];
}

const activeReasoning: ResearchActivityItem[] = [{
  kind: 'reasoning',
  title: 'Comparing the Evidence',
  content: 'Checking the strongest claims against the available sources.',
  queries: [],
  urls: [],
  files: [],
  isComplete: false,
}];

const activeWebResearch: ResearchActivityItem[] = [
  {
    kind: 'thinking',
    content: 'Finding authoritative sources before drafting the answer.',
    queries: [],
    urls: [],
    files: [],
    isComplete: true,
  },
  {
    kind: 'search',
    queries: [
      'retrieval practice learning outcomes systematic review',
      'spacing effect classroom study',
    ],
    urls: [
      'https://www.nature.com/',
      'https://www.science.org/',
      'https://www.apa.org/',
    ],
    files: [],
    isComplete: false,
  },
];

const activeDocumentReview: ResearchActivityItem[] = [{
  kind: 'vision',
  queries: [],
  urls: [],
  files: ['Lecture 08 — Memory.pdf', 'Course review notes.docx'],
  isComplete: false,
}];

const completedThinking: ResearchActivityItem[] = [
  {
    kind: 'reasoning',
    title: 'Framing the Answer',
    content: 'Separated the core concept from the supporting examples.',
    queries: [],
    urls: [],
    files: [],
    isComplete: true,
  },
  {
    kind: 'narration',
    content: 'Organized the response from definition to practical application.',
    queries: [],
    urls: [],
    files: [],
    isComplete: true,
  },
];

const completedResearch: ResearchActivityItem[] = [
  {
    kind: 'search',
    queries: ['testing effect learning research', 'spaced repetition evidence'],
    urls: ['https://www.nature.com/', 'https://www.apa.org/'],
    files: [],
    isComplete: true,
  },
  {
    kind: 'web',
    queries: [],
    urls: ['https://www.science.org/'],
    files: [],
    isComplete: true,
  },
];

const levelQuestion = {
  question: 'How deep should the explanation go?',
  options: [{ label: 'GCSE' }, { label: 'A-level' }, { label: 'University' }],
  multiSelect: false,
};

const questionStep = (status: 'pending' | 'answered' | 'skipped'): ResearchActivityItem => ({
  kind: 'question',
  queries: [],
  urls: [],
  files: [],
  isComplete: status !== 'pending',
  question: {
    id: `preview-${status}`,
    status,
    questions: [levelQuestion],
    answers: status === 'answered' ? [{ selected: ['A-level'] }] : undefined,
  },
});

const thinkingStep: ResearchActivityItem = {
  kind: 'thinking',
  content: 'The right depth depends on what they are studying for.',
  queries: [],
  urls: [],
  files: [],
  isComplete: true,
};

const previewStates: PreviewState[] = [
  {
    title: 'Initial Response',
    description: 'Shown before any reasoning or tool activity has arrived.',
    badge: 'Active · empty',
    isActive: true,
    items: [],
  },
  {
    title: 'Live Reasoning',
    description: 'A reasoning summary is streaming with a contextual status.',
    badge: 'Active · reasoning',
    isActive: true,
    items: activeReasoning,
  },
  {
    title: 'Web Research',
    description: 'Search queries and sources are accumulating in one research run.',
    badge: 'Active · web',
    isActive: true,
    items: activeWebResearch,
  },
  {
    title: 'Document Review',
    description: 'The assistant is inspecting one or more uploaded resources.',
    badge: 'Active · files',
    isActive: true,
    items: activeDocumentReview,
  },
  {
    title: 'Completed Thoughts',
    description: 'A finished reasoning run without external research sources.',
    badge: 'Complete · reasoning',
    isActive: false,
    items: completedThinking,
  },
  {
    title: 'Waiting on a Question',
    description: 'The answer paused on a question; it is a step in the activity.',
    badge: 'Paused · question',
    isActive: true,
    items: [thinkingStep, questionStep('pending')],
  },
  {
    title: 'Answered Question',
    description: 'The question and its answer stay in the timeline.',
    badge: 'Complete · question',
    isActive: false,
    items: [thinkingStep, questionStep('answered')],
  },
  {
    title: 'Completed Research',
    description: 'A finished run with source favicons and a browsable activity sheet.',
    badge: 'Complete · sources',
    isActive: false,
    items: completedResearch,
  },
];

const PreviewCard = ({ children, state }: { children: ReactNode; state: PreviewState }) => (
  <article className="research-preview-card">
    <header className="research-preview-card-header">
      <span className={`research-preview-badge${state.isActive ? ' is-active' : ''}`}>
        {state.badge}
      </span>
      <h2>{state.title}</h2>
      <p>{state.description}</p>
    </header>
    <div className="research-preview-stage">{children}</div>
  </article>
);

export const ResearchActivityPreview = () => (
  <main className="research-preview-page">
    <section className="research-preview-hero">
      <span className="research-preview-kicker">Component preview</span>
      <h1>Research Activity States</h1>
      <p>
        Live states animate continuously. Select any populated state to inspect
        the full activity sheet.
      </p>
    </section>

    <section className="research-preview-grid" aria-label="Research activity component states">
      {previewStates.map((state, index) => (
        <PreviewCard key={state.title} state={state}>
          <ResearchActivityDisplay
            items={state.items}
            messageId={`research-preview-${index + 1}`}
            isActive={state.isActive}
          />
        </PreviewCard>
      ))}
    </section>
  </main>
);
